import React, { useState, useEffect } from "react";
import { Box, Paper, Button, Typography, Chip, LinearProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

const humanFileSize = (size) => {
  if (!size && size !== 0) return "";
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return (size / Math.pow(1024, i)).toFixed(2) * 1 + [" B", " KB", " MB", " GB", " TB"][i];
};

const FileUploader = ({
  files: filesProp = [],
  setFiles = () => {},
  accept = "image/*,application/pdf",
  multiple = true,
  maxSizeMB = 20,
  label = "Attach files (images & PDF)",
  uploadProgress = null, // optional overall percent 0-100
}) => {
  const files = Array.isArray(filesProp) ? filesProp : Array.from(filesProp || []);
  const [dragActive, setDragActive] = useState(false);
  const [rejectionInfo, setRejectionInfo] = useState({ typeRejected: 0, sizeRejected: 0 });

  useEffect(() => {
    return () => {
      // revoke object URLs if any (not created here)
    };
  }, []);

  const handleFiles = (list) => {
    const arr = Array.from(list || []);
    // simple validation
    const maxBytes = maxSizeMB * 1024 * 1024;

    // Parse accept string into tokens (e.g. image/*,application/pdf,.jpg)
    const tokens = (accept || "").split(",").map((t) => t.trim()).filter(Boolean);
    const isAcceptedType = (file) => {
      if (!tokens.length) return true; // no restriction
      const ft = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      for (const tok of tokens) {
        if (tok === "*/*") return true;
        if (tok.endsWith("/*")) {
          const prefix = tok.split("/")[0];
          if (ft.startsWith(prefix + "/")) return true;
        } else if (tok.startsWith(".")) {
          if (name.endsWith(tok)) return true;
        } else {
          if (ft === tok) return true;
        }
      }
      return false;
    };

    const acceptedByType = arr.filter((f) => isAcceptedType(f));
    const rejectedByType = arr.filter((f) => !isAcceptedType(f));

    const accepted = acceptedByType.filter((f) => f.size <= maxBytes);
    const rejectedBySize = acceptedByType.filter((f) => f.size > maxBytes);

    // Update rejection info for UI feedback
    setRejectionInfo({ typeRejected: rejectedByType.length, sizeRejected: rejectedBySize.length });

    // Build combined list using current `files` prop (works whether parent uses setter or wrapper)
    const prevArr = Array.isArray(files) ? files : Array.from(files || []);
    const combined = multiple ? [...prevArr, ...accepted] : accepted.slice(0, 1);
    // de-duplicate by name+size
    const map = new Map();
    for (const f of combined) {
      map.set(`${f.name}_${f.size}`, f);
    }
    const result = Array.from(map.values());
    // Call setFiles with the final array (parent can be either a setter or a wrapper that expects the array)
    setFiles(result);
  };

  const onDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    if (e.type === "dragleave") setDragActive(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (index) => {
    const newFiles = (Array.isArray(files) ? files : Array.from(files || [])).filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  return (
    <Paper
      onDragEnter={onDrag}
      onDragLeave={onDrag}
      onDragOver={onDrag}
      onDrop={onDrop}
      sx={{ p: 2, borderRadius: 2, bgcolor: dragActive ? "#eaf4ff" : "#fafafa", border: "1px dashed #d0d7de" }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexDirection: "column" }}>
        <CloudUploadIcon sx={{ fontSize: 48, color: dragActive ? "primary.main" : "text.secondary" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Drag & drop files here, or click to choose. <strong>Allowed:</strong> images and PDF. Max {maxSizeMB} MB per file.
        </Typography>
        {((rejectionInfo.typeRejected || rejectionInfo.sizeRejected) > 0) && (
          <Typography variant="caption" sx={{ color: "error.main", mt: 0.5 }}>
            {rejectionInfo.typeRejected > 0 ? `${rejectionInfo.typeRejected} file(s) ignored due to unsupported type. ` : ""}
            {rejectionInfo.sizeRejected > 0 ? `${rejectionInfo.sizeRejected} file(s) ignored due to size > ${maxSizeMB}MB.` : ""}
          </Typography>
        )}

        <Button variant="outlined" component="label">
          Choose Files
          <input
            hidden
            multiple={multiple}
            accept={accept}
            type="file"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </Button>

        {Array.isArray(files) && files.length > 0 && (
          <Box sx={{ width: "100%", mt: 1, bgcolor: '#f7f7fa', border: '1px solid #e0e0e0', borderRadius: 2, p: 1 }}>
            {uploadProgress != null && (
              <Box sx={{ mb: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption">Uploading: {uploadProgress}%</Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {files.filter(f => f && typeof f === 'object' && f.name).map((f, i) => (
                <Box key={`${f.name}_${f.size}_${i}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    m: 0.5,
                    boxShadow: 0,
                  }}
                >
                  <InsertDriveFileIcon sx={{ fontSize: 20, color: '#757575', mr: 1 }} />
                  <Typography variant="body2" sx={{ mr: 1, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.name}>
                    {f.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', mr: 1 }}>
                    • {Math.round(f.size / 1024)} KB
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => removeFile(i)}
                    sx={{ minWidth: 0, p: 0.5, ml: 0.5, color: '#888' }}
                  >
                    ×
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default FileUploader;
